// ── Multi-scale rhythmic clock + Phase 1 FieldClockSystem ──
// Multi-scale clock is ported from curious-dusts/src/simulation/clock.ts
// (timeline-driven version, stripped of Transport/audio coupling).
// FieldClockSystem implements the locked Phase 1 state machine from
// docs/SPEC.md S3.

export interface Clock {
  readonly micro: number;   // sub-frame jitter
  readonly beat: number;    // 1s cycle (heart)
  readonly bar: number;     // 4s cycle (breath)
  readonly phrase: number;  // 16s cycle (mood)
  readonly macro: number;   // 60s cycle (drift)
}

interface MutableClock {
  micro: number;
  beat: number;
  bar: number;
  phrase: number;
  macro: number;
}

const _clock: MutableClock = { micro: 0, beat: 0, bar: 0, phrase: 0, macro: 0 };

export function updateClock(t: number, dance: number = 1): Clock {
  _clock.beat = Math.sin(t * Math.PI * 2.0) * dance;
  _clock.bar = Math.sin(t * Math.PI * 2.0 / 4.0) * dance;
  _clock.phrase = Math.sin(t * Math.PI * 2.0 / 16.0) * dance;
  _clock.macro = Math.sin(t * Math.PI * 2.0 / 60.0) * dance;
  _clock.micro = (Math.random() - 0.5) * 2.0 * dance * 0.15;
  return _clock;
}

export function heartbeat(t: number, intensity: number = 1): number {
  const c = updateClock(t, 1);
  const primary = (c.beat + 1) * 0.5;
  const depth = (c.bar + 1) * 0.5 * 0.3;
  const wobble = c.micro * 0.05;
  const shaped = Math.pow(primary, 0.7);
  return Math.min(1, Math.max(0, (shaped + depth + wobble) * intensity));
}

// ── FieldClockSystem (Phase 1 state machine) ───────────────────────
//
// Locked invariants (per docs/SPEC.md S3):
//   scrollTarget          = clamp(scrollY / scrollMax, 0, 1)
//   highestScrollProgress = max(prev, scrollTarget)
//   fieldStoryTarget      = highestScrollProgress
//   fieldStoryTime        = dampTowards(prev, fieldStoryTarget, dt, dampingMs)
//   highestStoryTime      = max(prev, fieldStoryTime)
//
// glassProgress           = scrollTarget   (bidirectional, snappy)
// fieldLifeTime           = monotonic wall clock (debug HUD only in P1)

export interface FieldClockState {
  glassProgress: number;
  highestScrollProgress: number;
  fieldStoryTarget: number;
  fieldStoryTime: number;
  highestStoryTime: number;
  fieldLifeTime: number;
}

export class FieldClockSystem {
  private state: FieldClockState = {
    glassProgress: 0,
    highestScrollProgress: 0,
    fieldStoryTarget: 0,
    fieldStoryTime: 0,
    highestStoryTime: 0,
    fieldLifeTime: 0,
  };

  /** Damping time constant in milliseconds. 300-500 ordinary, 700-900 ceremonial. */
  private dampingMs: number = 400;

  /** Standard tick: damped pursuit with exponential follow. */
  update(scrollY: number, scrollMax: number, dt: number): FieldClockState {
    const scrollTarget = scrollMax > 0
      ? Math.max(0, Math.min(1, scrollY / scrollMax))
      : 0;
    this.state.glassProgress = scrollTarget;

    this.state.highestScrollProgress = Math.max(
      this.state.highestScrollProgress,
      scrollTarget,
    );
    this.state.fieldStoryTarget = this.state.highestScrollProgress;

    // Exponential damped follow. tau = dampingMs / 1000 seconds.
    const tau = this.dampingMs / 1000;
    const alpha = tau > 0 ? 1 - Math.exp(-dt / tau) : 1;
    this.state.fieldStoryTime +=
      (this.state.fieldStoryTarget - this.state.fieldStoryTime) * alpha;

    // Numerical guard: highestStoryTime cannot retreat.
    this.state.highestStoryTime = Math.max(
      this.state.highestStoryTime,
      this.state.fieldStoryTime,
    );

    this.state.fieldLifeTime += dt;

    return this.state;
  }

  /** Degraded mode (mobile fallback): bypass damped pursuit, jump to semantic state. */
  semanticOnly(scrollY: number, scrollMax: number): FieldClockState {
    const scrollTarget = scrollMax > 0
      ? Math.max(0, Math.min(1, scrollY / scrollMax))
      : 0;
    this.state.glassProgress = scrollTarget;
    this.state.highestScrollProgress = Math.max(
      this.state.highestScrollProgress,
      scrollTarget,
    );
    this.state.fieldStoryTarget = this.state.highestScrollProgress;
    this.state.fieldStoryTime = this.state.highestScrollProgress;
    this.state.highestStoryTime = Math.max(
      this.state.highestStoryTime,
      this.state.fieldStoryTime,
    );
    return this.state;
  }

  /** Reduced-motion mode: snap to destination state (highestStoryTime = 1). */
  snapToDestination(): FieldClockState {
    this.state.glassProgress = 1;
    this.state.highestScrollProgress = 1;
    this.state.fieldStoryTarget = 1;
    this.state.fieldStoryTime = 1;
    this.state.highestStoryTime = 1;
    return this.state;
  }

  setDampingMs(ms: number): void {
    this.dampingMs = ms;
  }

  get(): FieldClockState {
    return this.state;
  }
}
